import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useSession } from './useSession'
import { LoadingBlock } from '../ui/Feedback'
import { useI18n } from '../i18n/LocaleContext'

/**
 * Client-side routing guard. The real access control is RLS on the database —
 * this only decides which screen a user lands on, so an unauthorised user sees
 * a clean redirect instead of a page full of empty query errors.
 */
export function RequireAuth({ children, adminOnly }: { children: ReactNode; adminOnly?: boolean }) {
  const session = useSession()
  const { t } = useI18n()

  if (session.status === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas">
        <LoadingBlock />
      </main>
    )
  }

  if (session.status === 'unauthenticated') return <Navigate to="/login" replace />
  if (session.status === 'unprovisioned' || session.status === 'no_subscription') {
    return <Navigate to="/paywall" replace />
  }

  if (adminOnly && !session.isAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas p-8">
        <p className="rounded-lg border border-DEFAULT bg-surface px-6 py-4 text-[13px] text-gray-700 shadow-card">
          {t.auth.forbidden}
        </p>
      </main>
    )
  }

  return <>{children}</>
}
